import React, { useState, useEffect, useRef } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import PropTypes from "prop-types";
import classNames from "classnames";
import { TimecardService } from "@services";
import utils from "@utils";

import {
	Attachment,
	ConfirmationModal,
	Loader,
	Modal,
	TimeInput,
	Tooltip,
} from "../../../../../components";
import "./EditTimecardModal.scss";

const EditTimecardModal = props => {
	const { lang } = global;
	const {
		show,
		onHide,
		onSave,
		clockInTime,
		clockOutTime,
		positionId,
		officer,
		notes,
		user,
		timecard,
		allowEditTimeCard,
		newDesign,
	} = props;

	const confirmationModal = useRef();
	const [clockInTimeModal, setClockInModal] = useState("");
	const [clockOutTimeModal, setClockOutModal] = useState("");
	const [notesModal, setNotesModal] = useState("");
	const [attachments, setAttachments] = useState([]);
	const [attachmentLoading, setAttachmentLoading] = useState(false);

	const { timecardAttachmentsInvoiceV4 } = useFlags();

	const hasTimecard = officer?.TimeCard?.Id || timecard?.Id;
	const canAddAttachment = timecardAttachmentsInvoiceV4 && hasTimecard;

	const getTimecardAttachments = async () => {
		try {
			setAttachmentLoading(true);
			const response = await TimecardService.getAttachmentsById(
				officer?.TimeCard?.Id || timecard?.Id,
			);
			setAttachments(response.data);
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Unable to retrieve timecard attachments"),
				timeOut: 1500,
			});
		} finally {
			setAttachmentLoading(false);
		}
	};

	useEffect(() => {
		if (show && (officer?.TimeCard?.Id || timecard?.Id) && timecardAttachmentsInvoiceV4) {
			getTimecardAttachments();
			return;
		}

		setAttachments([]);
	}, [show]);

	useEffect(() => {
		if (clockInTime) {
			setClockInModal(
				user.UserPreferences !== "useMilitaryTime"
					? utils.convertHour12to24(clockInTime)
					: clockInTime,
			);
		} else {
			setClockInModal("");
		}
	}, [clockInTime]);
	useEffect(() => {
		if (clockOutTime) {
			setClockOutModal(
				user.UserPreferences !== "useMilitaryTime"
					? utils.convertHour12to24(clockOutTime)
					: clockOutTime,
			);
		} else {
			setClockOutModal("");
		}
	}, [clockOutTime]);
	useEffect(() => {
		if (notes) {
			setNotesModal(notes);
		} else {
			setNotesModal("");
		}
	}, [show]);

	const validateDisableForm = () => {
		if (!allowEditTimeCard) {
			return true;
		}

		if (notesModal === "" || clockInTimeModal === "" || clockOutTimeModal === "") {
			return true;
		}
		return false;
	};

	const addAttachment = async e => {
		const input = e.target;
		const reader = new FileReader();
		reader.readAsDataURL(input.files[0]);
		reader.onload = async () => {
			try {
				setAttachmentLoading(true);
				await TimecardService.addAttachment(
					officer?.TimeCard?.Id || timecard?.Id,
					input.files[0],
				);
				getTimecardAttachments();
			} catch ({ response: { data } }) {
				utils.notify({
					type: "error",
					message: `${lang.t("Unable to add attachment")}, ${data}`,
					timeOut: 1500,
				});
			} finally {
				setAttachmentLoading(false);
			}
			// value needs to be cleared for reuploading of docs
			input.value = "";
		};
	};

	const deleteAttachment = async attachmentId => {
		try {
			setAttachmentLoading(true);
			await TimecardService.deleteAttachment(
				officer?.TimeCard?.Id || timecard?.Id,
				attachmentId,
			);
			getTimecardAttachments();
			utils.notify({
				type: "success",
				message: lang.t("Attachment deleted successfully"),
				timeOut: 1500,
			});
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Unable to delete attachment"),
				timeOut: 1500,
			});
		} finally {
			setAttachmentLoading(false);
		}
	};

	const showDeleteConfirmation = attachmentId => {
		confirmationModal.current.openModal({
			title: lang.t("Delete Attachment"),
			message: lang.t("Are you sure you want to delete this attachment_"),
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Attachment")}`,
			onConfirm: () => deleteAttachment(attachmentId),
		});
	};

	return (
		<Modal
			id="timecard-modal"
			show={show}
			onHide={() => onHide()}
		>
			<Modal.Header
				closeButton
				className="hidden-print"
			>
				<Modal.Title>
					{newDesign ? lang.t("Edit Timecard") : lang.t("Timecard Details")}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="container-fluid">
				<div style={{ marginLeft: "15px", marginRight: "15px" }}>
					{newDesign && (
						<>
							<div className="row label-control">
								<div className="col-xs-4 modal-labels">
									<strong>{lang.t("Timecard Clock In")}</strong>
								</div>

								<div className="col-xs-4 modal-labels">
									<strong>{lang.t("Timecard Clock Out")}</strong>
								</div>
							</div>
							<div className="row modal-control">
								<div className="col-xs-4">
									<TimeInput
										name="clockin"
										id="clockin"
										time={clockInTimeModal}
										className={classNames("form-control")}
										handleChange={e => setClockInModal(e.target.value)}
										user={user}
										disabled={!allowEditTimeCard}
									/>
								</div>
								<div className="col-xs-4">
									<TimeInput
										name="clockout"
										id="clockout"
										time={clockOutTimeModal}
										className={classNames("form-control")}
										handleChange={e => setClockOutModal(e.target.value)}
										user={user}
										disabled={!allowEditTimeCard}
									/>
								</div>
							</div>
							<div className="row">
								<div className="col-xs-12 label-control">
									<strong>{lang.t("Notes")}</strong>
								</div>
								<div className="col-xs-12">
									<textarea
										style={{ resize: "none" }}
										className="form-control"
										name="notes"
										value={notesModal}
										disabled={!allowEditTimeCard}
										onChange={e => setNotesModal(e.target.value)}
										placeholder={lang.t("Timecard notes placeholder")}
									/>
								</div>
							</div>
						</>
					)}
					{!newDesign && (
						<>
							<div className="row modal-control">
								<div className="col-xs-2 modal-labels">{lang.t("Clock-In")}* :</div>
								<div className="col-xs-4">
									<TimeInput
										name="clockin"
										id="clockin"
										time={clockInTimeModal}
										className={classNames("form-control")}
										handleChange={e => setClockInModal(e.target.value)}
										user={user}
										disabled={!allowEditTimeCard}
									/>
								</div>
							</div>
							<div className="row modal-control">
								<div className="col-xs-2 modal-labels">
									{lang.t("Clock-Out")}* :
								</div>
								<div className="col-xs-4">
									<TimeInput
										name="clockout"
										id="clockout"
										time={clockOutTimeModal}
										className={classNames("form-control")}
										handleChange={e => setClockOutModal(e.target.value)}
										user={user}
										disabled={!allowEditTimeCard}
									/>
								</div>
							</div>
							<div className="row">
								<div className="col-xs-12">{lang.t("Notes")}* :</div>
								<div className="col-xs-12">
									<textarea
										style={{ resize: "none" }}
										className="form-control"
										name="notes"
										value={notesModal}
										disabled={!allowEditTimeCard}
										onChange={e => setNotesModal(e.target.value)}
									/>
								</div>
							</div>
						</>
					)}

					<If condition={timecardAttachmentsInvoiceV4}>
						<div className="row margin-top-10">
							<div className="col-xs-12">
								{lang.t("Attachments")}:&nbsp;
								<If condition={attachments.length === 0}>
									<span>{lang.t("N/A")}</span>
								</If>
								<If condition={canAddAttachment}>
									<label
										htmlFor="upload-attachment"
										className="add-attachment-input"
									>
										<i className="rkf rkf-Paperclip" />
										<span>{lang.t("Add attachment")}</span>
										<input
											id="upload-attachment"
											type="file"
											accept=".pdf,.jpeg,.jpg,.png"
											onChange={addAttachment}
											className="hidden"
										/>
									</label>
								</If>
								<If condition={!canAddAttachment}>
									<Tooltip content={!hasTimecard ? "Attachment not found" : ""}>
										<span className="add-attachment-input disabled">
											<i className="rkf rkf-Paperclip" />
											<span>{lang.t("Add attachment")}</span>
										</span>
									</Tooltip>
								</If>
							</div>
							<div className="col-xs-12">
								<div className="attachments-container">
									<If condition={attachments.length > 0}>
										<For
											each="attachment"
											index="idx"
											of={attachments}
										>
											<div
												className="attachment-container"
												key={idx}
											>
												<Attachment
													key={idx}
													document={attachment}
													assetInfo={`${utils.formatDate(new Date(`${attachment.CreateDateUTC}Z`), "en-US", user.TimeZoneId, "2-digit")}`}
												/>
												<If
													condition={
														user.Id === attachment.CreatedByUserId
													}
												>
													<div className="attachment-actions-container">
														<button
															type="button"
															disabled={
																user.Id !==
																attachment.CreatedByUserId
															}
															className="btn btn-link"
															onClick={() =>
																showDeleteConfirmation(
																	attachment.Id,
																)
															}
														>
															<i className="rkf rkf-trash text-danger" />
														</button>
													</div>
												</If>
											</div>
										</For>
									</If>
								</div>
							</div>
						</div>
					</If>
					<ConfirmationModal ref={confirmationModal} />
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					id="button-go-back"
					className="btn btn-link hidden-print"
					type="button"
					onClick={() => onHide()}
				>
					{lang.t("Cancel")}
				</button>
				<button
					id="button-print-table"
					className="btn btn-primary hidden-print"
					type="button"
					disabled={validateDisableForm()}
					onClick={() =>
						onSave(positionId, officer, clockInTimeModal, clockOutTimeModal, notesModal)
					}
				>
					{lang.t("Save Changes")}
				</button>
			</Modal.Footer>
			<Loader active={attachmentLoading} />
		</Modal>
	);
};

EditTimecardModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	onSave: PropTypes.func.isRequired,
	clockInTime: PropTypes.string,
	clockOutTime: PropTypes.string,
	notes: PropTypes.string,
	officer: PropTypes.object,
	positionId: PropTypes.any,
	user: PropTypes.object.isRequired,
	timecard: PropTypes.object,
	allowEditTimeCard: PropTypes.bool,
	newDesign: PropTypes.bool,
};

EditTimecardModal.defaultProps = {
	show: false,
	onHide: () => {},
	clockInTime: "",
	officer: undefined,
	positionId: "",
	clockOutTime: "",
	notes: "",
	timecard: {},
	allowEditTimeCard: true,
	newDesign: false,
};

export default EditTimecardModal;
